"use client";

import { motion } from "framer-motion";
import { FaCloud, FaSync, FaChartLine, FaCogs, FaServer, FaShieldAlt, FaDatabase } from "react-icons/fa";
import { Link } from "react-router-dom";
import config from "../../../config/services.json";
import HeroSection from "../../../components/common/HeroSection";

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 }
};


export default function CloudSolutionsPage() {
  const { cloudProcess } = config
  return (
    <main className="min-h-screen bg-gradient-to-br from-white via-gray-50 to-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <HeroSection 
          title="Cloud Solutions"
          description="Scale efficiently, secure your data, and accelerate innovation with our enterprise-grade cloud infrastructure"
          showPattern={true}
        />
      </section>

      {/* Services Overview */}
      <section className="pb-12 sm:pb-16 md:pb-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="space-y-4 sm:space-y-5 md:space-y-6"
            >
              <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold">Enterprise Cloud Solutions for Modern Business</h2>
              <p className="text-base sm:text-lg text-gray-600">
                We deliver robust cloud infrastructure solutions that are secure, scalable, and cost-effective. 
                Our expert team leverages cutting-edge cloud technologies to transform your business operations 
                and drive digital innovation.
              </p>
              <div className="space-y-3 sm:space-y-4">
                {/* Checkmark items */}
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1 flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-sm sm:text-base text-gray-700">Scalable cloud infrastructure and deployment</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Enterprise-grade security and compliance</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">Multi-cloud and hybrid cloud solutions</p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center mt-1">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <p className="text-gray-700">24/7 monitoring and technical support</p>
                </div>
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="relative mt-8 md:mt-0"
            >
              <div className="relative h-[400px] sm:h-[500px] md:h-[600px] w-full perspective-[2000px]">
                <motion.div
                  animate={{
                    rotateY: [0, 10, 0],
                    rotateX: [0, 5, 0]
                  }}
                  transition={{
                    duration: 5,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{ transformStyle: "preserve-3d" }}
                  className="relative h-full"
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 to-purple-600/20 
                    rounded-3xl transform rotate-6"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-indigo-600/20 
                    rounded-3xl transform -rotate-3"></div>
                  <div className="relative h-full rounded-3xl overflow-hidden shadow-2xl bg-white/50 backdrop-blur-sm">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <motion.div
                        animate={{
                          y: [0, -20, 0],
                          scale: [1, 1.1, 1]
                        }}
                        transition={{
                          duration: 4,
                          repeat: Infinity,
                          ease: "easeInOut"
                        }}
                        className="text-8xl text-indigo-600"
                      >
                        <FaCloud />
                      </motion.div>
                    </div>
                  </div>
                </motion.div>
              </div>
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-indigo-600 rounded-full opacity-20"></div>
              <div className="absolute -top-6 -right-6 w-32 h-32 bg-purple-600 rounded-full opacity-20"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-12 sm:py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14 md:mb-16"
          >
            <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-5 md:mb-6">Our Cloud Services</h2>
            <p className="text-base sm:text-lg text-gray-600">
              We offer comprehensive cloud solutions to help your business thrive in the digital age.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {[
              {
                icon: <FaServer className="w-6 h-6" />,
                title: "Cloud Infrastructure",
                description: "Scalable and reliable cloud infrastructure solutions tailored to your business needs."
              },
              {
                icon: <FaShieldAlt className="w-6 h-6" />,
                title: "Security & Compliance",
                description: "Enterprise-grade security measures and compliance with industry standards."
              },
              {
                icon: <FaSync className="w-6 h-6" />,
                title: "Migration Services",
                description: "Seamless migration of your existing infrastructure to the cloud."
              },
              {
                icon: <FaChartLine className="w-6 h-6" />,
                title: "Performance Optimization",
                description: "Optimize your cloud resources for maximum performance and cost-efficiency."
              },
              {
                icon: <FaDatabase className="w-6 h-6" />,
                title: "Cloud Storage",
                description: "Secure and scalable storage solutions for your business data."
              },
              {
                icon: <FaCogs className="w-6 h-6" />,
                title: "Managed Services",
                description: "24/7 monitoring and management of your cloud infrastructure."
              }
            ].map((service, index) => (
              <motion.div
                key={service.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeIn}
                transition={{ delay: index * 0.1 }}
                whileHover={{ y: -10 }}
                className="bg-gradient-to-br from-white to-gray-50 p-6 sm:p-8 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 
                  text-white flex items-center justify-center mb-4 sm:mb-6">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="pt-12 sm:pt-16 md:pt-20 pb-40 sm:pb-50 md:pb-60">
        <div className="container mx-auto px-4">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
            className="text-center max-w-3xl mx-auto mb-12 sm:mb-14 md:mb-16"
          >
            <h2 className="text-2xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-5 md:mb-6">Our Cloud Implementation Process</h2>
            <p className="text-base sm:text-lg text-gray-600">
              We follow a systematic approach to deliver secure and scalable cloud solutions that transform your business infrastructure.
            </p>
          </motion.div>

          <div className="relative max-w-5xl mx-auto">
            <div className="space-y-6 sm:space-y-8 md:h-[600px] md:space-y-0">
              {cloudProcess.map((process, index) => (
                <motion.div
                  key={process.step}
                  className="block md:absolute md:inset-x-0"
                  style={{
                    top: `${index * 140}px`,
                    transformStyle: "preserve-3d"
                  }}
                  initial={{ opacity: 0, rotateX: 45, y: 100 }}
                  whileInView={{
                    opacity: 1,
                    rotateX: 0,
                    y: 0
                  }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.8,
                    delay: index * 0.2,
                    ease: "easeOut"
                  }}
                  whileHover={{
                    scale: 1.02,
                    rotateX: -5,
                    transition: { duration: 0.3 }
                  }}
                >
                  <div className={`bg-gradient-to-r ${process.color} p-6 sm:p-8 rounded-3xl shadow-2xl 
                    text-white relative overflow-hidden group`}>
                    <div className="absolute -right-8 -top-8 w-32 h-32 bg-white/10 rounded-full blur-2xl 
                      group-hover:scale-150 transition-transform duration-500" />
                    
                    <div className="flex items-start gap-6">
                      <div className="flex-shrink-0 w-16 h-16 bg-white/20 rounded-2xl flex items-center 
                        justify-center text-3xl backdrop-blur-sm">
                        {process.icon}
                      </div>
                      <div className="space-y-4">
                        <div className="flex items-center gap-3">
                          <span className="text-5xl font-bold opacity-50">{process.step}</span>
                          <h3 className="text-2xl font-bold">{process.title}</h3>
                        </div>
                        <p className="text-lg text-white/80">{process.description}</p>
                      </div>
                    </div>

                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0"
                      initial={{ x: "-100%" }}
                      whileHover={{ x: "100%" }}
                      transition={{ duration: 1 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 sm:py-16 md:py-20 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              className="text-2xl sm:text-3xl md:text-4xl font-bold mb-4 sm:mb-5 md:mb-6"
            >
              Ready to Transform Your Business with Cloud Solutions?
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.1 }}
              className="text-base sm:text-lg text-indigo-100 mb-8 sm:mb-9 md:mb-10"
            >
              Let's discuss how we can help you leverage cloud technology to scale your business and drive innovation.
            </motion.p>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <Link
                to="/contact"
                className="inline-flex items-center px-6 sm:px-7 md:px-8 py-3 sm:py-3.5 md:py-4 rounded-full bg-white text-indigo-600 
                  hover:shadow-lg hover:shadow-indigo-700/20 transition-all duration-300 text-base sm:text-lg font-semibold"
              >
                Schedule a Cloud Consultation
                <svg className="w-4 h-4 sm:w-5 sm:h-5 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}